import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { server } from "../../server";
import Loader from "../Layout/Loader";
import { AiOutlineCamera } from "react-icons/ai";
import { CloudArrowUpIcon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { loadUser } from "../../redux/actions/user";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ImageCropperModal from "../Image/ImageCropperModal";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from "../../utils/imageUploadPreparation";

const UserBanner = ({ isUser, user, setUser, onAvatarChange }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userBanner, setUserBanner] = useState(user.userBanner?.url || null);
  const [showBannerCropper, setShowBannerCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [showBannerMenu, setShowBannerMenu] = useState(false);
  const currentProfile = sessionStorage.getItem("currentProfile");
  const dispatch = useDispatch();
  const bannerMenuRef = useRef(null);
  const bannerInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    if (!showBannerMenu) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (bannerMenuRef.current && !bannerMenuRef.current.contains(event.target)) {
        setShowBannerMenu(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showBannerMenu]);

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (file) {
      try {
        const preparedFile = await prepareImageFileForUpload(file);
        setShowBannerMenu(false);
        setSelectedFile(preparedFile);
        setShowBannerCropper(true);
      } catch (error) {
        console.error("Error preparing user banner:", error);
        setSelectedFile(null);
        setShowBannerCropper(false);
        toast.error(getImagePreparationErrorMessage(error));
      }
    }
  };

  const updateBanner = (croppedFile) => {
    setCroppedFile(croppedFile); // Store the cropped banner file
    const previewUrl = URL.createObjectURL(croppedFile);
    setUserBanner(previewUrl);
    setShowSaveButton(true); // Show save button when a new image is uploaded
  };

  const handleSaveBanner = async () => {
    setIsLoading(true);
    setShowBannerMenu(false);
    try {
      const formData = new FormData();
      formData.append("userBanner", croppedFile);

      await axios.put(`${server}/user/update-banner`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
          "Current-Profile": currentProfile,
        },
      });
      dispatch(loadUser());
      toast.success("Banner updated successfully!");
      setShowSaveButton(false); // Hide save button after saving
      setUser({ ...user, userBanner: { url: userBanner } });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update banner");
    } finally {
      setIsLoading(false);
    }
  };

  const openFilePicker = (inputRef) => {
    inputRef.current?.click();
    setShowBannerMenu(false);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="user-banner-container">
      <img
        src={userBanner || "default-banner.png"}
        alt="User Banner"
        className="user-banner-image"
      />
      <div className="banner-overlay"></div>
      {isUser && (
        <div className="user-banner-controls">
          <input
            type="file"
            id="user-banner-background-input"
            ref={bannerInputRef}
            className="user-banner-file-input"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleBannerChange}
          />
          {onAvatarChange && (
            <input
              type="file"
              id="user-profile-avatar-menu-input"
              ref={avatarInputRef}
              className="user-banner-file-input"
              accept={IMAGE_ACCEPT_WITH_HEIC}
              onChange={onAvatarChange}
            />
          )}
          <div className="user-banner-action-menu-wrapper" ref={bannerMenuRef}>
            <button
              type="button"
              className="user-banner-menu-trigger"
              aria-label="Background image options"
              aria-expanded={showBannerMenu}
              onClick={() => setShowBannerMenu((isOpen) => !isOpen)}
            >
              <EllipsisHorizontalIcon className="user-banner-menu-trigger-icon" aria-hidden="true" />
            </button>
            {showBannerMenu && (
              <div className="user-banner-action-menu" role="menu">
                <button
                  type="button"
                  className="user-banner-action-menu-item"
                  role="menuitem"
                  onClick={() => openFilePicker(bannerInputRef)}
                >
                  <AiOutlineCamera className="user-banner-action-menu-icon" aria-hidden="true" />
                  <span>Edit background image</span>
                </button>
                {onAvatarChange && (
                  <button
                    type="button"
                    className="user-banner-action-menu-item"
                    role="menuitem"
                    onClick={() => openFilePicker(avatarInputRef)}
                  >
                    <AiOutlineCamera className="user-banner-action-menu-icon" aria-hidden="true" />
                    <span>Edit profile image</span>
                  </button>
                )}
                {showSaveButton && (
                  <button
                    type="button"
                    className="user-banner-action-menu-item"
                    onClick={handleSaveBanner}
                    disabled={!croppedFile || isLoading}
                    role="menuitem"
                  >
                    <CloudArrowUpIcon className="user-banner-action-menu-icon" aria-hidden="true" />
                    <span>Save background image</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Banner Image Cropper Modal */}
      {showBannerCropper && (
        <ImageCropperModal
          updateAvatar={updateBanner}
          closeModal={() => setShowBannerCropper(false)}
          initialImage={selectedFile}
          aspectRatio={4}
          minWidth={500}
          isAvatar={false}
          isPost={false}
          isLogo={false}
        />
      )}
    </div>
  );
};

export default UserBanner;
